/**
=========================================================
* Argon Dashboard 2 PRO MUI - v3.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-pro-mui
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// react-countup components
import Card from "@mui/material/Card";
import PropTypes from "prop-types";
import CountUp from "react-countup";

// prop-types is a library for typechecking of props

// @mui material components

// Argon Dashboard 2 PRO MUI components
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import { useArgonController } from "context";

function ClickableCounterCard({ color, count, title, description, prefix, suffix, onClick }) {
  const [controller] = useArgonController();
  const { darkMode } = controller;

  return (
    <Card
      onClick={onClick}
      sx={{
        cursor: onClick ? "pointer" : "default",
        transition: "transform 0.2s",
        background: darkMode ? "#1a1c2d" : "#e2e2ee",
        "&:hover": onClick
          ? {
              transform: "scale(1.02)",
              background: darkMode ? "#2a2d45" : "#ffffff",
              boxShadow: "0 8px 16px 0 rgba(0,0,0,0.1)",
            }
          : {},
      }}
    >
      <ArgonBox p={3} textAlign="center" lineHeight={1.25}>
        <ArgonTypography
          variant="h1"
          fontWeight="bold"
          sx={{
            background: "linear-gradient(310deg, rgb(90 110 180), rgb(54 55 109))",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            display: "inline-block",
          }}
        >
          {prefix && (
            <ArgonTypography component="span" variant="h5" fontWeight="bold">
              {prefix}
            </ArgonTypography>
          )}
          <ArgonBox display="inline-block" mx={0.5}>
            <CountUp end={count} duration={1} separator="," />
          </ArgonBox>
          {suffix && (
            <ArgonTypography component="span" variant="h5" fontWeight="bold">
              {suffix}
            </ArgonTypography>
          )}
        </ArgonTypography>
        <ArgonTypography variant="h6" fontWeight="bold" textTransform="capitalize">
          {title}
        </ArgonTypography>
        {description && (
          <ArgonTypography
            variant="button"
            fontWeight="regular"
            opacity={0.8}
            textTransform="capitalize"
          >
            {description}
          </ArgonTypography>
        )}
      </ArgonBox>
    </Card>
  );
}

// Setting default values for the props of DefaultCounterCard
ClickableCounterCard.defaultProps = {
  color: "info",
  prefix: "",
  suffix: "",
  description: "",
  onClick: undefined,
};

// Typechecking props for the BlogCard
ClickableCounterCard.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  count: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  prefix: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  suffix: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  onClick: PropTypes.func,
};

export default ClickableCounterCard;
